import React, { useCallback, useMemo, useState } from 'react'

import {
  ArrowLeftIcon,
  EllipsisIcon,
  EyeIcon,
  EyeOffIcon,
  GripVerticalIcon,
  XIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'

import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import SearchBar from '~/client/src/shared/components/SearchBar/SearchBar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { SavedViewEditableFields } from '~/client/src/shared/enums/SavedViewEditableFields'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { NOOP } from '~/client/src/shared/utils/noop'

import SavedViewFilters from './SavedViewFilters'
import type { PresetTab } from './SavedViews'
import { FilterTypeDescriptor } from './savedViewsTypes'
import { SavedView, ViewItem } from './savedViewsUtils'

import './SavedViews.scss'

const ALL_ITEMS_RADIO_VALUE = '__all_items__'

interface ManageSavedViewsDialogOwnProps {
  views: SavedView[]
  selectedViewId: string | null
  onClose: () => void
  onViewSelect: (viewId: string | null) => void
  isActiveUserAdmin?: boolean
  currentUserId?: string
  facets?: FilterTypeDescriptor[]
  groupingOptions?: IOptionItem[]
  groupingEnabled?: boolean
  onDeleteView?: (viewId: string) => Promise<void>
  onDuplicateView?: (viewId: string) => Promise<void>
  onRenameView?: (viewId: string, newName: string) => Promise<void>
  onToggleVisibility?: (viewId: string) => void
  onReorderViews?: (
    oldIndex: number,
    newIndex: number,
    targetSection: 'shown' | 'hidden',
  ) => void
  onUpdateViewAccess?: (viewId: string) => Promise<void>
  allViewsForDialog?: ViewItem[]
  presetTabs?: PresetTab[]
}

interface ManageSavedViewsDialogInjectedProps {
  projectMembersStore?: ProjectMembersStore
}

type ManageSavedViewsDialogProps = ManageSavedViewsDialogOwnProps &
  ManageSavedViewsDialogInjectedProps

enum SavedViewsSections {
  Shown = 'shown',
  Hidden = 'hidden',
}

enum SavedViewActions {
  duplicate = 'duplicate',
  delete = 'delete',
}

enum SavedViewsPopups {
  none = 'none',
  filters = 'filters',
}

const SAVED_VIEWS_LIST_PRESET_ITEM_ID_PREFIX = 'saved-views-list-item-preset'

const SortableItem = SortableElement(({ item }) => (
  <div className="tw:w-full tw:inline-block tw:align-top tw:z-[120] tw:h-[40px] tw:px-1.5">
    {item}
  </div>
))

const SortableList = SortableContainer(
  ({ items, setHoveringSection, isSortDisabled }) => {
    const { presetItems, shownItems, hiddenItems } = items

    return (
      <div className="tw:flex-1 tw:flex tw:flex-col">
        <div
          onMouseEnter={setHoveringSection.bind(null, SavedViewsSections.Shown)}
        >
          <div className="tw:flex tw:items-center tw:[&>*]:flex-1 tw:text-title-sm tw:text-foreground tw:h-[40px] tw:mx-4">
            {Localization.translator.listSections.shown}
          </div>
          <div className="tw:flex-1 tw:flex tw:flex-col">
            {presetItems.map((item, index) => (
              <SortableItem
                key={`preset-saved-view-${index}`}
                index={index}
                disabled={true}
                item={item}
              />
            ))}
          </div>
          <div className="tw:flex-1 tw:flex tw:flex-col">
            {shownItems.map((item, index) => (
              <SortableItem
                key={`saved-view-${index}`}
                index={presetItems?.length + index}
                disabled={isSortDisabled}
                item={item}
              />
            ))}
          </div>
        </div>
        <div
          className="tw:flex-1 tw:flex tw:flex-col"
          onMouseEnter={setHoveringSection.bind(
            null,
            SavedViewsSections.Hidden,
          )}
        >
          <div className="tw:flex tw:items-center tw:[&>*]:flex-1 tw:text-title-sm tw:text-foreground tw:h-[40px] tw:mx-4">
            {Localization.translator.listSections.hidden}
          </div>
          <div className="tw:flex-1 tw:flex tw:flex-col">
            {hiddenItems.map((item, index) => (
              <SortableItem
                key={`hidden-saved-view-${index}`}
                index={presetItems?.length + shownItems?.length + index}
                item={item}
              />
            ))}
          </div>
        </div>
      </div>
    )
  },
)

const ManageSavedViewsDialogComponent: React.FC<
  ManageSavedViewsDialogProps
> = ({
  views,
  selectedViewId,
  onClose,
  onViewSelect,
  projectMembersStore,
  isActiveUserAdmin = false,
  currentUserId,
  facets = [],
  groupingOptions = [],
  groupingEnabled = true,
  onDeleteView,
  onDuplicateView,
  onRenameView,
  onToggleVisibility,
  onReorderViews,
  onUpdateViewAccess,
  allViewsForDialog = [],
  presetTabs,
}) => {
  const [searchKey, setSearchKey] = useState('')
  const [hoveringSection, setHoveringSection] = useState<SavedViewsSections>(
    SavedViewsSections.Shown,
  )
  const [openPopupSavedViewId, setOpenPopupSavedViewId] = useState<
    string | null
  >(null)
  const [openPopup, setOpenPopup] = useState<SavedViewsPopups>(
    SavedViewsPopups.none,
  )
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [editingField, setEditingField] =
    useState<SavedViewEditableFields | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [deletingViewId, setDeletingViewId] = useState<string | null>(null)

  const isSortDisabled = !!searchKey

  const getUserForFilter = useCallback(
    (userId?: string): User => {
      if (!userId || !projectMembersStore) {
        return User.service
      }
      return projectMembersStore.getById(userId) || User.service
    },
    [projectMembersStore],
  )

  const getAppliedFiltersByViewId = useCallback(
    (viewId: string): { [facetKey: string]: Set<string> } => {
      const view = views.find(v => v.id === viewId)
      if (!view) return {}

      const appliedFilters: { [facetKey: string]: Set<string> } = {}
      view.filtersByFilterType?.forEach(filter => {
        if (filter?.type && filter?.values) {
          appliedFilters[filter.type] = new Set(
            filter.values.filter((v): v is string => v != null),
          )
        }
      })
      return appliedFilters
    },
    [views],
  )

  const getSelectedFilterCountByViewId = useCallback(
    (viewId: string): number => {
      const view = views.find(v => v.id === viewId)
      if (!view) return 0

      return (
        view.filtersByFilterType?.reduce((sum, filter) => {
          return sum + (filter?.values?.filter(v => v != null).length || 0)
        }, 0) || 0
      )
    },
    [views],
  )

  const getGroupByCaptionByViewId = useCallback(
    (viewId: string): string => {
      const view = views.find(v => v.id === viewId)
      if (!view || !view.groupBy) return Localization.translator.none

      const option = groupingOptions.find(opt => opt.key === view.groupBy)
      return option?.label || view.groupBy
    },
    [views, groupingOptions],
  )

  const getSavedViewById = useCallback(
    (viewId: string): SavedView | undefined => {
      return views.find(v => v.id === viewId)
    },
    [views],
  )

  const onChangeSearchKey = useCallback((newSearchKey: string) => {
    setSearchKey(newSearchKey)
  }, [])

  const handleSetHoveringSection = useCallback(
    (section: SavedViewsSections) => {
      setHoveringSection(section)
    },
    [],
  )

  const closeSavedViewFiltersCount = useCallback(() => {
    setOpenPopupSavedViewId(null)
  }, [])

  const closeSavedViewsPopup = useCallback((event: Event) => {
    event?.stopPropagation()
    setOpenPopupSavedViewId(null)
    setOpenPopup(SavedViewsPopups.none)
  }, [])

  const onSelectSavedView = useCallback(
    (savedViewId: string) => {
      const isEditing = editingViewId !== null
      if (isEditing) {
        return
      }

      onViewSelect(savedViewId === ALL_ITEMS_RADIO_VALUE ? null : savedViewId)
    },
    [editingViewId, onViewSelect],
  )

  const onSavedViewNameClick = useCallback(
    (id: string) => () => {
      const isEditing = editingViewId !== null
      if (isEditing) {
        return
      }

      setEditingViewId(id)
      setEditingField(SavedViewEditableFields.Name)
      const view = getSavedViewById(id)
      setEditingValue(view?.name || '')
    },
    [editingViewId, getSavedViewById],
  )

  const onSavedViewNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditingValue(event.target.value)
    },
    [],
  )

  const onSavedViewNameInputBlur = useCallback(() => {
    if (editingViewId && editingValue && onRenameView) {
      onRenameView(editingViewId, editingValue)
    }
    setEditingViewId(null)
    setEditingField(null)
    setEditingValue('')
  }, [editingViewId, editingValue, onRenameView])

  const onChangeSavedViewAccess = useCallback(
    (id: string) => {
      const isEditing = editingViewId !== null
      if (isEditing || !onUpdateViewAccess) {
        return
      }

      onUpdateViewAccess(id)
    },
    [editingViewId, onUpdateViewAccess],
  )

  const handleSavedViewDuplication = useCallback(
    async (savedViewId: string) => {
      if (onDuplicateView) {
        await onDuplicateView(savedViewId)
      }
    },
    [onDuplicateView],
  )

  const onSavedViewFiltersCountClick = useCallback(
    (id: string) => (event: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation()

      if (openPopupSavedViewId === id) {
        return
      }

      setOpenPopupSavedViewId(id)
      setOpenPopup(SavedViewsPopups.filters)
    },
    [openPopupSavedViewId],
  )

  const performSavedViewAction = useCallback(
    (action: SavedViewActions, savedViewId: string) => {
      switch (action) {
        case SavedViewActions.delete:
          setDeletingViewId(savedViewId)
          return

        case SavedViewActions.duplicate:
          handleSavedViewDuplication(savedViewId)
          return
      }
    },
    [handleSavedViewDuplication],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (deletingViewId && onDeleteView) {
      await onDeleteView(deletingViewId)
      setDeletingViewId(null)
    }
  }, [deletingViewId, onDeleteView])

  const handleDeleteCancel = useCallback(() => {
    setDeletingViewId(null)
  }, [])

  const toggleItemVisibility = useCallback(
    (savedViewId: string) => {
      if (onToggleVisibility) {
        onToggleVisibility(savedViewId)
      }
    },
    [onToggleVisibility],
  )

  const getSavedViewActionCaption = (action: SavedViewActions): string => {
    switch (action) {
      case SavedViewActions.delete:
        return Localization.translator.delete

      case SavedViewActions.duplicate:
        return Localization.translator.duplicate

      default:
        throw new Error(`${action} is unhandled`)
    }
  }

  const canDeleteView = useCallback(
    (itemId: string): boolean => {
      if (isActiveUserAdmin) return true
      const view = allViewsForDialog.find(v => v.id === itemId)
      return view?.userId === currentUserId
    },
    [isActiveUserAdmin, currentUserId, allViewsForDialog],
  )

  const renderViewActions = useCallback(
    (itemId: string) => {
      const actions = Object.values(SavedViewActions).filter(action => {
        if (action === SavedViewActions.delete) {
          return canDeleteView(itemId)
        }
        return true
      })

      return actions.map(savedViewAction => (
        <DropdownMenuItem
          key={savedViewAction}
          onClick={performSavedViewAction.bind(null, savedViewAction, itemId)}
        >
          {getSavedViewActionCaption(savedViewAction)}
        </DropdownMenuItem>
      ))
    },
    [performSavedViewAction, canDeleteView],
  )

  const renderItems = useCallback(
    (
      items: ViewItem[],
      section: SavedViewsSections,
      isPresetSavedView: boolean = false,
    ) => {
      const isEditing = editingViewId !== null

      return items.map(item => {
        const isPopupOpen = openPopupSavedViewId === item.id

        const isEditingItemName =
          isEditing &&
          editingViewId === item.id &&
          editingField === SavedViewEditableFields.Name

        const isSharingDisabled =
          !isActiveUserAdmin ||
          (!isEditingItemName &&
            isEditing &&
            editingViewId === item.id &&
            editingField === SavedViewEditableFields.IsPublic)

        const shouldHighlightItem =
          isPopupOpen && openPopup !== SavedViewsPopups.none

        return (
          <div
            key={
              item.id ||
              `${SAVED_VIEWS_LIST_PRESET_ITEM_ID_PREFIX}-${item.name}`
            }
            className={classList({
              'tw:border-0 tw:border-b tw:border-solid tw:border-border/40 tw:hover:bg-muted tw:flex tw:items-center': true,
              'tw:bg-muted tw:hover:bg-muted': shouldHighlightItem,
            })}
          >
            <span
              className={classList({
                'dragging-trigger tw:grow-0 tw:p-2.5': true,
                'not-visible': isSortDisabled || isPresetSavedView,
                'grab-cursor': !isPresetSavedView,
              })}
            >
              <GripVerticalIcon size={20} />
            </span>
            <RadioGroup
              className="tw:w-10 tw:shrink-0"
              value={selectedViewId ?? ALL_ITEMS_RADIO_VALUE}
              onValueChange={onSelectSavedView}
            >
              <Radio
                value={item.id ?? ALL_ITEMS_RADIO_VALUE}
                disabled={section === SavedViewsSections.Hidden}
              />
            </RadioGroup>
            <div
              className={classList({
                'saved-views-list-item-name tw:inline-block tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[160px] tw:max-w-[160px] tw:grow-0 tw:pl-2': true,
                'text-cursor editable': !isPresetSavedView,
                editing: isEditingItemName,
              })}
              onClick={isPresetSavedView ? NOOP : onSavedViewNameClick(item.id)}
            >
              {isEditingItemName ? (
                <StruxhubInput
                  value={editingValue}
                  isRequiredTextHidden={true}
                  isLabelHidden={true}
                  isHelperTextHidden={true}
                  autoFocus={true}
                  onChange={onSavedViewNameChange}
                  onBlur={onSavedViewNameInputBlur}
                />
              ) : (
                <span className="tw:max-w-[152px] tw:align-middle tw:inline-block tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis">
                  {item.name}
                </span>
              )}
            </div>
            <div
              className={classList({
                'tw:inline-block tw:min-w-[100px] tw:max-w-[100px] tw:grow-0 tw:pl-2 tw:static': true,
                pointer: !isPresetSavedView,
              })}
              onClick={onSavedViewFiltersCountClick(item.id)}
            >
              <span className="tw:leading-10">
                {getSelectedFilterCountByViewId(item.id)}
              </span>
            </div>
            <div
              className={classList({
                'tw:inline-block tw:min-w-[160px] tw:max-w-[160px] tw:h-[40px] tw:grow-0 tw:pl-2 tw:relative': true,
                'pointer editable': !isPresetSavedView,
                'default-cursor': isPresetSavedView,
              })}
            >
              <span className="tw:leading-10">
                <Switch
                  checked={item.isPublic}
                  onCheckedChange={onChangeSavedViewAccess.bind(null, item.id)}
                  disabled={isPresetSavedView || isSharingDisabled}
                />
              </span>
            </div>
            <div className="tw:inline-block tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[200px] tw:max-w-[200px] tw:grow-0 tw:pl-2">
              <UserProfilePreview
                user={getUserForFilter(item.userId)}
                isUserNameClickable={false}
                isBriefInfoHidden={true}
                withEmployeeId={false}
                showBoldName={false}
                avatarSize={AvatarSize.Tiny}
                className="tw:inline-flex tw:align-middle"
                contentContainerClassName="tw:text-body-sm tw:text-foreground mw160"
              />
            </div>
            <div
              className={classList({
                'tw:inline-block tw:min-w-[40px] tw:max-w-[40px] tw:h-[40px] tw:grow-0 tw:p-2.5': true,
                pointer: !isPresetSavedView,
                'not-allowed': isPresetSavedView,
              })}
              onClick={
                isPresetSavedView
                  ? NOOP
                  : toggleItemVisibility.bind(null, item.id)
              }
            >
              {isPresetSavedView && (
                <EyeIcon
                  className="tw:text-muted-foreground tw:opacity-40"
                  size={20}
                />
              )}
              {!isPresetSavedView && section === SavedViewsSections.Shown && (
                <EyeIcon
                  className="tw:text-muted-foreground tw:hover:text-foreground"
                  size={20}
                />
              )}
              {!isPresetSavedView && section === SavedViewsSections.Hidden && (
                <EyeOffIcon
                  className="tw:text-muted-foreground tw:opacity-60 tw:hover:text-foreground"
                  size={20}
                />
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                nativeButton={false}
                render={
                  <div
                    className={classList({
                      'tw:inline-block tw:min-w-[40px] tw:max-w-[40px] tw:h-[40px] tw:grow-0 tw:p-2.5 tw:relative': true,
                      pointer: !isPresetSavedView,
                      'not-allowed': isPresetSavedView,
                    })}
                  />
                }
              >
                <EllipsisIcon
                  className={classList({
                    'tw:text-muted-foreground': true,
                    'tw:opacity-30': isPresetSavedView,
                  })}
                  size={20}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="end">
                {renderViewActions(item.id)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      })
    },
    [
      editingViewId,
      editingField,
      editingValue,
      openPopupSavedViewId,
      openPopup,
      selectedViewId,
      isSortDisabled,
      isActiveUserAdmin,
      getUserForFilter,
      onSelectSavedView,
      onSavedViewNameClick,
      onSavedViewNameChange,
      onSavedViewNameInputBlur,
      onSavedViewFiltersCountClick,
      onChangeSavedViewAccess,
      toggleItemVisibility,
      getSelectedFilterCountByViewId,
      renderViewActions,
    ],
  )

  const items = useMemo(() => {
    const presetViewItems: ViewItem[] = presetTabs?.length
      ? presetTabs
          .filter(tab =>
            tab.title.toLowerCase().includes(searchKey.toLowerCase()),
          )
          .map((tab, index) => ({
            id: tab.id,
            name: tab.title,
            isPublic: true,
            filtersByFilterType: null,
            groupBy: null,
            isHidden: false,
            order: -(presetTabs.length - index),
          }))
      : Localization.translator.all_items
            .toLowerCase()
            .includes(searchKey.toLowerCase())
        ? [
            {
              id: null,
              name: Localization.translator.all_items,
              isPublic: true,
              filtersByFilterType: null,
              groupBy: null,
              isHidden: false,
              order: -1,
            },
          ]
        : []

    const filteredViews = allViewsForDialog.filter(view =>
      view.name.toLowerCase().includes(searchKey.toLowerCase()),
    )

    const shownItems = filteredViews.filter(view => !view.isHidden)

    const hiddenItems = filteredViews.filter(view => view.isHidden)

    return {
      hiddenItems: renderItems(hiddenItems, SavedViewsSections.Hidden),
      shownItems: renderItems(shownItems, SavedViewsSections.Shown),
      presetItems: renderItems(presetViewItems, SavedViewsSections.Shown, true),
    }
  }, [allViewsForDialog, searchKey, renderItems, presetTabs])

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const presetCount = items.presetItems.length
      const adjustedOldIndex = oldIndex - presetCount
      const adjustedNewIndex = newIndex - presetCount

      if (onReorderViews) {
        onReorderViews(
          adjustedOldIndex,
          adjustedNewIndex,
          hoveringSection as 'shown' | 'hidden',
        )
      }
    },
    [hoveringSection, onReorderViews, items],
  )

  const isFiltersPopupOpen =
    openPopupSavedViewId && openPopup === SavedViewsPopups.filters

  if (isFiltersPopupOpen) {
    const item = getSavedViewById(openPopupSavedViewId)
    return (
      <div className="view-dialog saved-views-dialog view-dialog-details tw:text-primary tw:bg-background tw:border tw:border-solid tw:border-border tw:rounded-lg tw:shadow-[0px_4px_8px_3px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)] tw:max-h-[calc(100dvh-170px)] tw:w-[805px] tw:!h-fit">
        <div className="tw:flex tw:items-center tw:h-12 bb-light-cool-grey">
          <span className="tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-12 tw:h-12 tw:cursor-pointer tw:p-2">
            <ArrowLeftIcon
              className="tw:text-muted-foreground"
              onClick={closeSavedViewFiltersCount}
            />
          </span>
          <div className="tw:flex-1 tw:text-title-sm tw:text-foreground tw:text-center tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:mr-[50px]">
            {item?.name}
          </div>
        </div>
        <SavedViewFilters
          groupBy={getGroupByCaptionByViewId(openPopupSavedViewId)}
          appliedFilters={getAppliedFiltersByViewId(openPopupSavedViewId)}
          facets={facets}
          groupingEnabled={groupingEnabled}
          className="tw:h-fit tw:max-h-[470px] overflow-hidden-scrollbar tw:overflow-y-auto"
        />
      </div>
    )
  }

  return (
    <div className="view-dialog saved-views-dialog tw:text-primary tw:bg-background tw:border tw:border-solid tw:border-border tw:rounded-lg tw:shadow-[0px_4px_8px_3px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)] tw:h-[560px] tw:max-h-[calc(100dvh-170px)] tw:w-[805px]">
      <div className="tw:flex tw:items-center tw:h-12">
        <span className="tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-12 tw:h-12 tw:cursor-pointer tw:p-2">
          <XIcon onClick={onClose} />
        </span>
        <div className="tw:flex-1 tw:text-title-md tw:text-foreground tw:text-center tw:mr-12">
          {Localization.translator.savedViews}
        </div>
      </div>
      <SearchBar
        className="tw:mx-4 tw:my-2 ba-light-grey"
        value={searchKey}
        onChange={onChangeSearchKey}
        onReset={onChangeSearchKey.bind(null, '')}
        onTagClick={NOOP}
        customPlaceholder={Localization.translator.savedViewsDialog.searchHint}
      />
      <div className="tw:border-0 tw:border-b tw:border-solid tw:border-border/40 tw:flex tw:items-center tw:[&>*]:flex-1 tw:align-top tw:h-[40px] tw:mx-1.5">
        <div className="tw:inline-block tw:text-title-sm tw:text-foreground tw:leading-10 tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[160px] tw:max-w-[160px] tw:grow-0 tw:pl-2 tw:ml-[80px]">
          {Localization.translator.savedViewsDialog.columns.viewName}
        </div>
        <div className="tw:inline-block tw:text-title-sm tw:text-foreground tw:leading-10 tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[100px] tw:max-w-[100px] tw:grow-0 tw:pl-2">
          {Localization.translator.savedViewsDialog.columns.filterCount}
        </div>
        <div className="tw:inline-block tw:text-title-sm tw:text-foreground tw:leading-10 tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[160px] tw:max-w-[160px] tw:grow-0 tw:pl-2">
          {Localization.translator.savedViewsDialog.columns.access}
        </div>
        <div className="tw:inline-block tw:text-title-sm tw:text-foreground tw:leading-10 tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:min-w-[200px] tw:max-w-[200px] tw:grow-0 tw:pl-2 tw:mr-[80px]">
          {Localization.translator.savedViewsDialog.columns.creator}
        </div>
      </div>
      <div
        className="tw:h-[470px] tw:max-h-[calc(100%-150px)] tw:overflow-y-scroll"
        onScroll={closeSavedViewsPopup.bind(null)}
      >
        <div className="tw:flex-1 tw:flex tw:flex-col tw:pb-3 saved-views-list tw:text-body-md tw:text-foreground">
          <div className="tw:flex-1 tw:flex tw:flex-col tw:h-full sortable-list-holder">
            <SortableList
              items={items}
              onSortEnd={onSortEnd}
              axis="y"
              setHoveringSection={handleSetHoveringSection}
              isSortDisabled={isSortDisabled}
              distance={2}
            />
          </div>
        </div>
      </div>
      {deletingViewId && (
        <ConfirmDialog
          useRedConfirmationButton
          title={Localization.translator.deleteView}
          isOpen={true}
          onCancelClicked={handleDeleteCancel}
          onDoneClicked={handleDeleteConfirm}
          doneTitle={Localization.translator.delete}
        >
          <div className="tw:text-body-md tw:text-foreground tw:mb-2.5">
            {Localization.translator.deleteViewQuestion(
              getSavedViewById(deletingViewId)?.name,
            )}
          </div>
        </ConfirmDialog>
      )}
    </div>
  )
}

const ManageSavedViewsDialog = inject('projectMembersStore')(
  observer(ManageSavedViewsDialogComponent),
)

export default ManageSavedViewsDialog
